import React, { useState, useEffect, useCallback } from "react";
import { getAllRecipientAttachments } from "../api/signRequests";
import { Document, Page } from "react-pdf";
import JSZip from "jszip";
import { getSignedUrl } from "../utils/signedUrl";
// Icons are implemented as inline SVGs in the component

const RecipientAttachmentsView = ({ signRequestId, isVisible }) => {
  const [attachments, setAttachments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRecipients, setExpandedRecipients] = useState({});
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'
  const [viewerModal, setViewerModal] = useState({ isOpen: false, file: null });
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [pdfViewerState, setPdfViewerState] = useState({
    scale: 1,
    rotation: 0,
    currentPage: 1,
    totalPages: 0,
    loading: false,
  });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState({});

  useEffect(() => {
    if (isVisible && signRequestId) {
      fetchAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, signRequestId]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!viewerModal.isOpen) return;

      // Close modal
      if (event.key === "Escape") {
        closeViewerModal();
        return;
      }

      // File viewer shortcuts (PDF and Image)
      if (
        (viewerModal.file?.mimeType?.includes("pdf") ||
          viewerModal.file?.mimeType?.includes("image")) &&
        !pdfLoadError
      ) {
        switch (event.key) {
          case "+":
          case "=":
            event.preventDefault();
            zoomIn();
            break;
          case "-":
            event.preventDefault();
            zoomOut();
            break;
          case "0":
            event.preventDefault();
            resetZoom();
            break;
          case "r":
          case "R":
            event.preventDefault();
            rotateClockwise();
            break;
          default:
            // PDF-specific shortcuts
            if (viewerModal.file?.mimeType?.includes("pdf")) {
              switch (event.key) {
                case "ArrowLeft":
                  event.preventDefault();
                  prevPage();
                  break;
                case "ArrowRight":
                  event.preventDefault();
                  nextPage();
                  break;
                case "Home":
                  event.preventDefault();
                  goToPage(1);
                  break;
                case "End":
                  event.preventDefault();
                  goToPage(pdfViewerState.totalPages);
                  break;
                default:
                  break;
              }
            }
            break;
        }
      }
    };

    if (viewerModal.isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [
    viewerModal.isOpen,
    viewerModal.file,
    pdfLoadError,
    pdfViewerState.totalPages,
  ]);

  const fetchAttachments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllRecipientAttachments(signRequestId);
      setAttachments(response.data);
    } catch (err) {
      console.error("Error fetching attachments:", err);
      setError("Failed to load attachments");
    } finally {
      setLoading(false);
    }
  }, [signRequestId]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes("pdf")) {
      return (
        <svg
          className="w-6 h-6 text-red-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else if (mimeType?.includes("image")) {
      return (
        <svg
          className="w-6 h-6 text-blue-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else if (mimeType?.includes("word") || mimeType?.includes("document")) {
      return (
        <svg
          className="w-6 h-6 text-blue-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-6 h-6 text-gray-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
  };

  const downloadFile = async (attachment) => {
    try {
      const signedUrl = await getSignedUrl(attachment.filePath);
      // Create download link
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = attachment.originalFileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const downloadRecipientZip = async (recipientData) => {
    const recipientId = recipientData.recipient._id;
    const recipientName = recipientData.recipient.name;

    setDownloadingZip((prev) => ({ ...prev, [recipientId]: true }));

    try {
      const zip = new JSZip();

      // Download all files for this recipient
      const downloadPromises = recipientData.attachments.map(
        async (attachment) => {
          try {
            const fileUrl = await getSignedUrl(attachment.filePath);
            const response = await fetch(fileUrl);
            if (!response.ok)
              throw new Error(
                `Failed to download ${attachment.originalFileName}`
              );

            const blob = await response.blob();
            zip.file(attachment.originalFileName, blob);
          } catch (error) {
            console.error(
              `Error downloading ${attachment.originalFileName}:`,
              error
            );
            // Add a placeholder file to indicate download failure
            zip.file(
              `ERROR_${attachment.originalFileName}`,
              `Failed to download: ${error.message}`
            );
          }
        }
      );

      await Promise.all(downloadPromises);

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Create download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${recipientName.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}_attachments.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error creating zip file:", error);
      alert("Failed to create zip file. Please try again.");
    } finally {
      setDownloadingZip((prev) => ({ ...prev, [recipientId]: false }));
    }
  };

  const toggleRecipient = (recipientId) => {
    setExpandedRecipients((prev) => ({
      ...prev,
      [recipientId]: !prev[recipientId],
    }));
  };

  const viewFile = async (attachment) => {
    if (
      attachment.mimeType?.includes("pdf") ||
      attachment.mimeType?.includes("image")
    ) {
      // Fetch signed URL before opening modal
      try {
        const signedUrl = await getSignedUrl(attachment.filePath);
        // Open in full-screen modal for PDFs and images
        setViewerModal({ isOpen: true, file: { ...attachment, signedUrl } });
        setPdfLoadError(false);
        setPdfLoading(true);
        setPdfViewerState({
          scale: 1,
          rotation: 0,
          currentPage: 1,
          totalPages: 0,
          loading: false,
        });
      } catch (error) {
        console.error("Error fetching signed URL:", error);
        setPdfLoadError(true);
        setPdfLoading(false);
      }
    } else {
      // Download for other file types
      downloadFile(attachment);
    }
  };

  const closeViewerModal = () => {
    setViewerModal({ isOpen: false, file: null });
    setPdfLoadError(false);
    setPdfLoading(false);
    setPdfViewerState({
      scale: 1,
      rotation: 0,
      currentPage: 1,
      totalPages: 0,
      loading: false,
    });
  };

  const openPdfInNewTab = async () => {
    try {
      const fileUrl = await getSignedUrl(viewerModal.file.filePath);
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("Error fetching signed URL:", error);
    }
  };

  const getFileUrl = () => {
    // Return the signed URL that was fetched when the modal was opened
    return viewerModal.file?.signedUrl || "";
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfViewerState((prev) => ({
      ...prev,
      totalPages: numPages,
      loading: false,
    }));
    setPdfLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
    setPdfLoadError(true);
    setPdfLoading(false);
  };

  // PDF Viewer Controls
  const zoomIn = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.25, 3),
    }));
  };

  const zoomOut = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.25, 0.5),
    }));
  };

  const resetZoom = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      scale: 1,
    }));
  };

  const rotateClockwise = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const goToPage = (page) => {
    setPdfViewerState((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages)),
    }));
  };

  const nextPage = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      currentPage: Math.min(prev.currentPage + 1, prev.totalPages),
    }));
  };

  const prevPage = () => {
    setPdfViewerState((prev) => ({
      ...prev,
      currentPage: Math.max(prev.currentPage - 1, 1),
    }));
  };

  const getFileTypeColor = (mimeType) => {
    if (mimeType?.includes("pdf")) return "text-red-500";
    if (mimeType?.includes("image")) return "text-blue-500";
    if (mimeType?.includes("word") || mimeType?.includes("document"))
      return "text-blue-600";
    if (mimeType?.includes("excel") || mimeType?.includes("spreadsheet"))
      return "text-green-600";
    if (mimeType?.includes("powerpoint") || mimeType?.includes("presentation"))
      return "text-orange-500";
    return "text-gray-500";
  };

  if (!isVisible) return null;

  return (
    <div className="bg-white m-w-[500px]">
      <div className="">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-green-100">
              <svg
                className="h-5 w-5 text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Recipient Attachments
            </h3>
          </div>

          {/* View Mode Toggle */}
          {/* <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="List View"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === "grid"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="Grid View"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
          </div> */}
        </div>
      </div>
      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-600">Loading attachments...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchAttachments}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : attachments ? (
          <div className="">
            {/* Attachments by Recipient */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {attachments.attachmentsByRecipient.map(
                (recipientData, index) => {
                  const recipientId = recipientData.recipient._id;
                  const isExpanded = expandedRecipients[recipientId];

                  return (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Recipient Header - Clickable */}
                      <div
                        className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => toggleRecipient(recipientId)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {recipientData.recipient.name.charAt(0)}
                          </div>
                          <div>
                            <h5 className="font-semibold text-gray-900 text-sm">
                              {recipientData.recipient.name}
                            </h5>
                            <p className="text-xs text-gray-600">
                              {recipientData.recipient.email}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadRecipientZip(recipientData);
                            }}
                            disabled={downloadingZip[recipientId]}
                            className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 hover:bg-green-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download all files as ZIP"
                          >
                            {downloadingZip[recipientId] ? (
                              <svg
                                className="w-3 h-3 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            ) : (
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            )}
                          </button>
                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                            {recipientData.attachments.length} files
                          </span>
                          <svg
                            className={`w-4 h-4 text-gray-500 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Collapsible Content */}
                      {isExpanded && (
                        <div className="p-3 border-t border-gray-200">
                          {recipientData.attachments.length > 0 ? (
                            <div
                              className={`${
                                viewMode === "grid"
                                  ? "grid grid-cols-1 sm:grid-cols-2 gap-2"
                                  : "space-y-2"
                              }`}
                            >
                              {recipientData.attachments.map(
                                (attachment, attIndex) => (
                                  <div
                                    key={attIndex}
                                    className={`flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${
                                      viewMode === "grid"
                                        ? "flex-col text-center"
                                        : ""
                                    }`}
                                  >
                                    <div
                                      className={`flex items-center gap-2 flex-1 min-w-0 ${
                                        viewMode === "grid" ? "flex-col" : ""
                                      }`}
                                    >
                                      <div
                                        className={`${getFileTypeColor(
                                          attachment.mimeType
                                        )} ${
                                          viewMode === "grid" ? "mb-2" : ""
                                        }`}
                                      >
                                        {getFileIcon(attachment.mimeType)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p
                                          className={`font-medium text-gray-900 truncate ${
                                            viewMode === "grid"
                                              ? "text-xs"
                                              : "text-xs"
                                          }`}
                                        >
                                          {attachment.originalFileName}
                                        </p>
                                        <div
                                          className={`flex items-center gap-3 text-xs text-gray-500 ${
                                            viewMode === "grid"
                                              ? "flex-col gap-1"
                                              : ""
                                          }`}
                                        >
                                          <span>
                                            {formatFileSize(
                                              attachment.fileSize
                                            )}
                                          </span>
                                          <span>
                                            {formatDate(attachment.createdAt)}
                                          </span>
                                          {attachment.fileDescription && (
                                            <span className="italic">
                                              "{attachment.fileDescription}"
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div
                                      className={`flex items-center gap-1 ${
                                        viewMode === "grid" ? "mt-2" : "ml-2"
                                      }`}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          viewFile(attachment);
                                        }}
                                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                        title={
                                          attachment.mimeType?.includes(
                                            "pdf"
                                          ) ||
                                          attachment.mimeType?.includes("image")
                                            ? "View file"
                                            : "Download file"
                                        }
                                      >
                                        {attachment.mimeType?.includes("pdf") ||
                                        attachment.mimeType?.includes(
                                          "image"
                                        ) ? (
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 italic text-center py-4">
                              No attachments uploaded
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Full-Screen File Viewer Modal */}
      {viewerModal.isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={closeViewerModal}
        >
          <div
            className="w-full h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getFileIcon(viewerModal.file.mimeType)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {viewerModal.file.originalFileName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(viewerModal.file.fileSize)} •{" "}
                    {formatDate(viewerModal.file.createdAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {(viewerModal.file.mimeType?.includes("pdf") ||
                  viewerModal.file.mimeType?.includes("image")) && (
                  <button
                    onClick={openPdfInNewTab}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Open in new tab"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => downloadFile(viewerModal.file)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download file"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={closeViewerModal}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close viewer"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* File Viewer Controls */}
            {(viewerModal.file.mimeType?.includes("pdf") ||
              viewerModal.file.mimeType?.includes("image")) &&
              !pdfLoadError && (
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Left Controls - Zoom */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={zoomOut}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Zoom Out"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">
                        {Math.round(pdfViewerState.scale * 100)}%
                      </span>
                      <button
                        onClick={zoomIn}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Zoom In"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={resetZoom}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Reset Zoom"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Center Controls - Page Navigation (PDF only) */}
                    {viewerModal.file.mimeType?.includes("pdf") && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={prevPage}
                          disabled={pdfViewerState.currentPage <= 1}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Previous Page"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max={pdfViewerState.totalPages || 1}
                            value={pdfViewerState.currentPage}
                            onChange={(e) =>
                              goToPage(parseInt(e.target.value) || 1)
                            }
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                          />
                          <span className="text-sm text-gray-600">
                            of {pdfViewerState.totalPages || "?"}
                          </span>
                        </div>
                        <button
                          onClick={nextPage}
                          disabled={
                            pdfViewerState.currentPage >=
                            pdfViewerState.totalPages
                          }
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Next Page"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* Right Controls - Rotate */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={rotateClockwise}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Rotate Clockwise"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                      <span className="text-sm text-gray-600">
                        {pdfViewerState.rotation}°
                      </span>
                    </div>
                  </div>
                </div>
              )}

            {/* Content */}
            <div className="flex-1 flex items-center justify-center bg-gray-100 p-4">
              {viewerModal.file.mimeType?.includes("pdf") ? (
                <div className="w-full h-full bg-white rounded-lg shadow-lg overflow-hidden">
                  {!pdfLoadError ? (
                    <div className="w-full h-full relative">
                      {pdfLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                          <div className="text-center">
                            <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
                            <p className="text-gray-600">Loading PDF...</p>
                          </div>
                        </div>
                      )}
                      <div className="w-full h-full overflow-auto">
                        <div className="min-h-full flex items-center justify-center p-4">
                          <div
                            className="shadow-lg"
                            style={{
                              transform: `scale(${pdfViewerState.scale}) rotate(${pdfViewerState.rotation}deg)`,
                              transformOrigin: "center center",
                            }}
                          >
                            <Document
                              file={getFileUrl()}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              loading={
                                <div className="flex items-center justify-center p-8">
                                  <div className="text-center">
                                    <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
                                    <p className="text-gray-600">
                                      Loading PDF...
                                    </p>
                                  </div>
                                </div>
                              }
                              error={
                                <div className="flex items-center justify-center p-8">
                                  <div className="text-center">
                                    <p className="text-red-600 mb-4">
                                      Failed to load PDF
                                    </p>
                                    <button
                                      onClick={openPdfInNewTab}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                      Open in New Tab
                                    </button>
                                  </div>
                                </div>
                              }
                            >
                              <Page
                                pageNumber={pdfViewerState.currentPage}
                                width={Math.min(800, window.innerWidth - 200)}
                                height={Math.min(
                                  1000,
                                  window.innerHeight - 200
                                )}
                                renderAnnotationLayer={false}
                                renderTextLayer={false}
                              />
                            </Document>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8">
                      <div className="text-center">
                        <svg
                          className="w-16 h-16 text-red-500 mx-auto mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          PDF Preview Not Available
                        </h3>
                        <p className="text-gray-600 mb-4">
                          The PDF cannot be displayed in the preview. This might
                          be due to browser security settings or file
                          permissions.
                        </p>
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={openPdfInNewTab}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            Open in New Tab
                          </button>
                          <button
                            onClick={() => downloadFile(viewerModal.file)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : viewerModal.file.mimeType?.includes("image") ? (
                <div className="w-full h-full overflow-auto">
                  <div className="min-h-full flex items-center justify-center p-4">
                    <div
                      className="shadow-lg"
                      style={{
                        transform: `scale(${pdfViewerState.scale}) rotate(${pdfViewerState.rotation}deg)`,
                        transformOrigin: "center center",
                      }}
                    >
                      <img
                        src={getFileUrl()}
                        alt={viewerModal.file.originalFileName}
                        className="max-w-full max-h-full object-contain rounded-lg"
                        style={{
                          maxHeight: "100vh",
                          maxWidth: "100vw",
                        }}
                        onError={() => {
                          console.error("Image failed to load");
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {viewerModal.file.fileDescription && (
                    <span className="italic">
                      "{viewerModal.file.fileDescription}"
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {(viewerModal.file.mimeType?.includes("pdf") ||
                    viewerModal.file.mimeType?.includes("image")) &&
                  !pdfLoadError ? (
                    <div className="flex items-center gap-4">
                      <span>Shortcuts:</span>
                      <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">
                        +/-
                      </kbd>
                      <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">
                        0
                      </kbd>
                      <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">
                        R
                      </kbd>
                      {viewerModal.file.mimeType?.includes("pdf") && (
                        <>
                          <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">
                            ←/→
                          </kbd>
                        </>
                      )}
                      <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">
                        ESC
                      </kbd>
                    </div>
                  ) : (
                    <span>
                      Press{" "}
                      <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">
                        ESC
                      </kbd>{" "}
                      to close
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipientAttachmentsView;
